import VIDABOO_CONSTANTS from '../config/consts';

const service = {};

const privateState = {
  registeredHandlePermissionChangeFuncs: {},
  handlePermissionChange: () => {},
  getPermissionsStatusesQuietly: async () => {},
};

privateState.handlePermissionChange = (permissionType, permissionStatus) => {
  Object.values(privateState.registeredHandlePermissionChangeFuncs).forEach(
    (handlePermissionChangeFunc) => {
      handlePermissionChangeFunc(permissionType, permissionStatus);
    },
  );
};

privateState.getPermissionsStatusesQuietly = async (permissionsType = []) => {
  //getting navigator.permissions.query takes around 200ms, so we save local copy and follow changes
  return new Promise((resolve, reject) => {
    if (
      permissionsType.length &&
      navigator.permissions &&
      navigator.permissions.query
    ) {
      (async () => {
        let permissionsStatusesObj = {};
        for (const permissionType of permissionsType) {
          permissionsStatusesObj[permissionType] =
            VIDABOO_CONSTANTS.PERMISSION_STATUS.NOT_SUPPORTED;
          try {
            const permissionStatus = await navigator.permissions.query({
              name: permissionType,
            });
            permissionsStatusesObj[permissionType] = permissionStatus.state;
            //onchange is supported only where navigator.permissions.query is supported
            permissionStatus.onchange = () =>
              privateState.handlePermissionChange(
                permissionType,
                permissionStatus.state,
              );
          } catch (error) {
            console.debug(
              `An error occurred while checking the ${permissionType} permission: `,
              error,
            );
            reject(error);
          }
        }
        // console.debug('Permission result: ', permissionsStatusesObj);
        resolve(permissionsStatusesObj);
      })();
    } else {
      reject('Permission API is not supported in this browser.');
    }
  });
};

service.quietGetPermissions = async (permissionsType = []) => {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const permissionsStatusesObj =
          await privateState.getPermissionsStatusesQuietly(permissionsType);
        resolve(permissionsStatusesObj);
      } catch (error) {
        reject(error);
      }
    })();
  });
};

service.loudGetPermissions = (
  constraints = {audio: true, video: true},
  getStream = false,
) => {
  return new Promise((resolve, reject) => {
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (getStream) {
          resolve(stream);
        } else {
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          resolve(true);
        }
      } catch (error) {
        if (
          VIDABOO_CONSTANTS.PERMISSION_ERROR_MSG.map(
            (errMsgObj) => errMsgObj.errMsg,
          ).includes(error.message)
        ) {
          service.handleOSLevelPermission(constraints);
        }
        reject(error);
      }
    })();
  });
};

service.handleOSLevelPermission = async (constraints) => {
  if (constraints.video) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({video: true});
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      privateState.handlePermissionChange(
        VIDABOO_CONSTANTS.PERMISSION_TYPES.CAMERA,
        VIDABOO_CONSTANTS.PERMISSION_STATUS.GRANTED,
      );
    } catch (error) {
      privateState.handlePermissionChange(
        VIDABOO_CONSTANTS.PERMISSION_TYPES.CAMERA,
        VIDABOO_CONSTANTS.PERMISSION_STATUS.NOT_SUPPORTED,
      );
    }
  }
  if (constraints.audio) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      privateState.handlePermissionChange(
        VIDABOO_CONSTANTS.PERMISSION_TYPES.MICROPHONE,
        VIDABOO_CONSTANTS.PERMISSION_STATUS.GRANTED,
      );
    } catch (error) {
      privateState.handlePermissionChange(
        VIDABOO_CONSTANTS.PERMISSION_TYPES.MICROPHONE,
        VIDABOO_CONSTANTS.PERMISSION_STATUS.NOT_SUPPORTED,
      );
    }
  }
};

service.registerHandlePermissionChangeFunc = (callbackFunc) => {
  const callbackFunctionId =
    new Date().getTime() + '_' + Math.floor(Math.random() * 1e8);
  privateState.registeredHandlePermissionChangeFuncs[callbackFunctionId] =
    callbackFunc;
  return () => {
    privateState.registeredHandlePermissionChangeFuncs[callbackFunctionId] &&
      delete privateState.registeredHandlePermissionChangeFuncs[
        callbackFunctionId
      ];
  };
};

const resourcePermissionsService = {...service};
export default resourcePermissionsService;
